import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, CheckCircle2, FileText, Award } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORY_LABEL } from "@/lib/categories";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/courses/${slug}`)
      .then(({ data }) => { setCourse(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (course) {
      document.title = course.seo_title || `${course.title} | EhliyetKurs`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", course.seo_description || course.short_desc);
    }
  }, [course]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-32 text-neutral-500">Yükleniyor…</div>;
  if (!course) return (
    <div className="max-w-7xl mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-4xl mb-4">Kurs bulunamadı</h1>
      <Link to="/kurslar" className="btn-accent">Tüm Kurslar</Link>
    </div>
  );

  return (
    <div data-testid="course-detail-page">
      <section className="relative min-h-[70vh] flex items-end bg-neutral-950 overflow-hidden">
        <img src={course.hero_image} alt={course.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 pt-32 w-full text-white">
          <Link to="/kurslar" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white mb-6" data-testid="back-to-courses">
            <ArrowLeft size={16} /> Kurslar
          </Link>
          <div className="overline text-[color:var(--ek-accent)] mb-4">{CATEGORY_LABEL[course.category]}</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            {course.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-neutral-300">
            <span className="flex items-center gap-2"><Clock size={16} /> {course.duration}</span>
            <span className="flex items-center gap-2"><Award size={16} /> {course.certificate}</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <div>
            <div className="overline text-[color:var(--ek-accent)] mb-4">Program Detayı</div>
            <p className="text-lg md:text-xl text-neutral-800 leading-relaxed">{course.description}</p>
          </div>

          {course.requirements?.length > 0 && (
            <div>
              <h2 className="font-display text-3xl tracking-tight mb-6 flex items-center gap-3">
                <FileText size={22} className="text-[color:var(--ek-accent)]" /> Gerekli Belgeler
              </h2>
              <ul className="space-y-3">
                {course.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-neutral-700">
                    <CheckCircle2 size={20} className="text-[color:var(--ek-accent)] mt-0.5 flex-shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {course.exam_process && (
            <div>
              <h2 className="font-display text-3xl tracking-tight mb-6">Sınav Süreci</h2>
              <p className="text-neutral-700 leading-relaxed">{course.exam_process}</p>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 self-start">
          <div className="bg-[color:var(--ek-surface-light)] rounded-2xl p-8">
            <div className="overline text-[color:var(--ek-accent)] mb-2">Hızlı Kayıt</div>
            <h3 className="font-display text-2xl mb-4">Bu kursa başvur</h3>
            <p className="text-sm text-neutral-600 mb-6">Formu doldur, uzman danışmanımız 24 saat içinde arasın.</p>
            <button
              onClick={() => navigate(`/basvuru?kurs=${course.slug}`)}
              data-testid="course-detail-apply-btn"
              className="btn-accent w-full justify-center"
            >
              Hemen Başvur
            </button>
            <div className="mt-6 pt-6 border-t border-black/5 text-sm text-neutral-600 space-y-2">
              <div className="flex justify-between"><span>Süre</span><span className="font-medium text-neutral-900">{course.duration}</span></div>
              <div className="flex justify-between"><span>Kategori</span><span className="font-medium text-neutral-900">{CATEGORY_LABEL[course.category]}</span></div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
